import { useEffect } from 'react';

const EPSILON = 1;
const INK = /[^\s​﻿]/;
const REPLACED_TAGS = new Set(['IMG', 'SVG', 'CANVAS', 'VIDEO', 'IFRAME', 'OBJECT', 'HR']);

export function useOverflowMarker(contentRef, containerRef, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return;
    if (!contentRef.current || !containerRef.current) return;

    const marked = new Set();
    const range = document.createRange();

    const paddingBox = (el) => {
      const styles = getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      return { top: rect.top + (parseFloat(styles.borderTopWidth) || 0), bottom: rect.bottom - (parseFloat(styles.borderBottomWidth) || 0) };
    };

    // The band of the page that content may occupy. Bottom is the usable content area
    // (template height minus header / footer); top is where `.page-content` clips.
    const pageBand = (container, content) => {
      const styles = getComputedStyle(container);
      if (styles.overflowY === 'visible') return { top: -Infinity, bottom: Infinity };
      const cssHeight = parseFloat(styles.height);
      if (!cssHeight) return { top: -Infinity, bottom: Infinity };
      const innerHeight = cssHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
      const headerHeight = container.querySelector('.area-header')?.getBoundingClientRect().height || 0;
      const footerHeight = container.querySelector('.area-footer')?.getBoundingClientRect().height || 0;
      return {
        top: container.getBoundingClientRect().top + parseFloat(styles.paddingTop),
        bottom: content.getBoundingClientRect().top + (innerHeight - headerHeight - footerHeight),
      };
    };

    // The band this leaf's content may occupy before something actually clips it.
    const clipBand = (leaf, container, page) => {
      let { top, bottom } = page;
      for (let el = leaf; el && el !== container; el = el.parentElement) {
        if (getComputedStyle(el).overflowY === 'visible') continue;
        const box = paddingBox(el);
        top = Math.max(top, box.top);
        bottom = Math.min(bottom, box.bottom);
      }
      return { top, bottom };
    };

    // Union of every rendered rect inside the leaf, whitespace included — one layout read.
    const contentExtent = (leaf) => {
      range.selectNodeContents(leaf);
      let top = Infinity;
      let bottom = -Infinity;
      for (const rect of range.getClientRects()) {
        if (rect.height <= 0) continue;
        top = Math.min(top, rect.top);
        bottom = Math.max(bottom, rect.bottom);
      }
      return top === Infinity ? null : { top, bottom };
    };

    const hasClippedInk = (leaf, band) => {
      const walker = document.createTreeWalker(leaf, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (node.nodeType === Node.TEXT_NODE) return INK.test(node.data) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
          return REPLACED_TAGS.has(node.tagName) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        },
      });

      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        let rects;
        if (node.nodeType === Node.TEXT_NODE) {
          range.selectNodeContents(node);
          rects = range.getClientRects();
        } else {
          rects = [node.getBoundingClientRect()];
        }
        for (const rect of rects) {
          if (rect.height <= 0) continue;
          if (rect.top < band.top - EPSILON || rect.bottom > band.bottom + EPSILON) return true;
        }
      }
      return false;
    };

    const checkOverflow = () => {
      if (!contentRef.current || !containerRef.current) return;
      const content = contentRef.current;
      const container = containerRef.current;

      marked.forEach((el) => el.classList.remove('content-overflow'));
      marked.clear();
      container.classList.remove('page-content-overflow');
      container.style.removeProperty('--content-overflow-line');

      const page = pageBand(container, content);
      let pageLine = Infinity;

      for (const leaf of content.querySelectorAll('.layout-leaf')) {
        const band = clipBand(leaf, container, page);
        const extent = contentExtent(leaf);
        if (!extent) continue;
        if (extent.top >= band.top - EPSILON && extent.bottom <= band.bottom + EPSILON) continue;

        if (!hasClippedInk(leaf, band)) continue;
        leaf.classList.add('content-overflow');
        marked.add(leaf);
        // The marker sits on the leaf's bottom edge. When the leaf itself runs past the
        // cutoff that marker is clipped away unseen, so the page draws the line instead.
        if (paddingBox(leaf).bottom > band.bottom + EPSILON) pageLine = Math.min(pageLine, band.bottom);
      }

      if (pageLine !== Infinity) {
        container.style.setProperty('--content-overflow-line', `${pageLine - container.getBoundingClientRect().top - 3}px`);
        container.classList.add('page-content-overflow');
      }
    };

    let rafId = null;
    let disposed = false;
    const schedule = () => {
      if (disposed || rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        checkOverflow();
      });
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(contentRef.current);
    ro.observe(containerRef.current);

    const mo = new MutationObserver(schedule);
    mo.observe(contentRef.current, { childList: true, subtree: true, characterData: true });

    const root = contentRef.current.getRootNode();
    const styleEls = root.querySelectorAll?.('style') ?? [];
    const styleMo = new MutationObserver(schedule);
    styleEls.forEach((s) => styleMo.observe(s, { childList: true, characterData: true, subtree: true }));

    // Images and fonts settle asynchronously and reflow a leaf's contents without resizing
    // `.area-content` / `.page-content`, so neither observer above would fire. `load` does
    // not bubble — the capture phase is what reaches it.
    const content = contentRef.current;
    content.addEventListener('load', schedule, true);
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener('loadingdone', schedule);

    return () => {
      disposed = true;
      content.removeEventListener('load', schedule, true);
      document.fonts?.removeEventListener('loadingdone', schedule);
      ro.disconnect();
      mo.disconnect();
      styleMo.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [contentRef, containerRef, enabled]);
}
